import React from 'react';
import { DollarSign, AlertCircle, CheckCircle, Loader2, ChevronDown, ChevronUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { SOW } from '../../models/sow';
import { JobEstimate, EstimateLineItem, Material } from '../../models/workItem';

interface EvolvingEstimateProps {
  sow: SOW;
  onStartQuestionnaire?: () => void;
  onRefineEstimate?: () => void;
  onGenerateEstimate?: () => void;
  onViewBreakdown?: () => void;
  generatingQuickEstimate?: boolean;
  generatingProjectEstimate?: boolean;
  questionnairesLoaded?: boolean;
  questionnaireStats?: {
    pending: number;
    inProgress: number;
    completed: number;
  };
}

export const EvolvingEstimate: React.FC<EvolvingEstimateProps> = ({
  sow,
  onStartQuestionnaire,
  onRefineEstimate,
  onGenerateEstimate,
  onViewBreakdown,
  generatingQuickEstimate,
  generatingProjectEstimate,
  questionnairesLoaded,
  questionnaireStats
}) => {
  const [showBreakdown, setShowBreakdown] = React.useState(false);
  const [expandedJobs, setExpandedJobs] = React.useState<Set<string>>(new Set());

  // Determine current stage
  const hasQuickEstimate = !!sow.quickEstimate;
  const hasDetailedEstimate = !!sow.totalEstimate || !!sow.projectPlanId;
  const questionnaireComplete = sow.questionnaireCompleted;
  const isRefining = sow.status === 'estimating' && questionnaireComplete;
  const allQuestionnairesComplete = questionnaireStats &&
    questionnaireStats.pending === 0 && questionnaireStats.inProgress === 0;

  // Stage A: Before Initial Questionnaire
  if (!questionnaireComplete && sow.status === 'initialQuestionnaire') {
    return (
      <Card>
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <DollarSign className="h-4 w-4 md:h-5 md:w-5" />
            Get Your Estimate
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0 space-y-4">
          <div className="text-center py-6">
            <div className="mb-4">
              <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-blue-50 mb-3">
                <DollarSign className="h-8 w-8 text-blue-600" />
              </div>
            </div>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Complete Initial Questionnaire
            </h3>
            <p className="text-sm text-gray-600 mb-6 max-w-md mx-auto">
              Answer 10 quick questions to get a ballpark estimate for your project.
              Takes about 3 minutes.
            </p>
            <Button
              onClick={onStartQuestionnaire}
              size="lg"
              className="w-full md:w-auto"
            >
              Start Questionnaire →
            </Button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Stage A.5: Generating Initial Estimate (questionnaire complete, but no estimate yet)
  if (questionnaireComplete && !hasQuickEstimate && (generatingQuickEstimate || sow.status === 'initialQuestionnaire' || sow.status === 'estimating')) {
    return (
      <Card>
        <CardHeader className="p-4 md:p-6">
          <CardTitle className="flex items-center gap-2 text-base md:text-lg">
            <DollarSign className="h-4 w-4 md:h-5 md:w-5" />
            Generating Your Estimate
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0">
          <div className="text-center py-8">
            <Loader2 className="h-12 w-12 animate-spin mx-auto mb-4 text-blue-600" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              Analyzing Your Project...
            </h3>
            <p className="text-sm text-gray-600 max-w-md mx-auto">
              We're processing your questionnaire responses and uploaded images to create an accurate ballpark estimate.
              This usually takes 10-30 seconds.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Stage B: Initial Estimate (Quick Estimate)
  if (hasQuickEstimate && !hasDetailedEstimate) {
    const qe = sow.quickEstimate!;

    // Calculate totals from breakdown if not available at top level
    const breakdown = qe.breakdown || [];
    const totalCostLow = qe.totalCostLow || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.totalLow || 0), 0);
    const totalCostHigh = qe.totalCostHigh || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.totalHigh || 0), 0);
    const totalLaborLow = qe.totalLaborLow || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.laborCostLow || 0), 0);
    const totalLaborHigh = qe.totalLaborHigh || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.laborCostHigh || 0), 0);
    const totalMaterialLow = qe.totalMaterialLow || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.materialCostLow || 0), 0);
    const totalMaterialHigh = qe.totalMaterialHigh || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.materialCostHigh || 0), 0);

    return (
      <Card>
        <CardHeader className="p-4 md:p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <DollarSign className="h-4 w-4 md:h-5 md:w-5" />
              Your Estimate
            </CardTitle>
            <Badge className="bg-amber-100 text-amber-800 border-amber-200">
              ⚠️ Ballpark
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0 space-y-4">
          {/* Total Range - BIG */}
          <div className="text-center py-4">
            <div className="text-3xl md:text-4xl font-bold text-gray-900">
              ${totalCostLow.toLocaleString()} - ${totalCostHigh.toLocaleString()}
            </div>
            <div className="text-sm text-gray-600 mt-1">Estimated Project Cost</div>
          </div>

          {/* Labor & Material Split */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs text-gray-600 mb-1">💼 Labor</div>
              <div className="text-base md:text-lg font-semibold text-gray-900">
                ${totalLaborLow.toLocaleString()} - ${totalLaborHigh.toLocaleString()}
              </div>
            </div>
            <div className="p-3 md:p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs text-gray-600 mb-1">🔨 Materials</div>
              <div className="text-base md:text-lg font-semibold text-gray-900">
                ${totalMaterialLow.toLocaleString()} - ${totalMaterialHigh.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Job Breakdown - Collapsible */}
          {qe.breakdown && qe.breakdown.length > 0 && (
            <div className="space-y-2">
              <button
                onClick={() => setShowBreakdown(!showBreakdown)}
                className="flex items-center justify-between w-full px-4 py-3 text-sm font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 border border-blue-200 rounded-lg transition-colors"
              >
                <span>See Breakdown</span>
                {showBreakdown ? (
                  <ChevronUp className="h-4 w-4" />
                ) : (
                  <ChevronDown className="h-4 w-4" />
                )}
              </button>

              {showBreakdown && (
                <div className="space-y-2">
                  {qe.breakdown.map((job, idx) => {
                    const jobId = job.id || job.jobId || `job-${idx}`;
                    const jobLabel = job.label || job.jobLabel || 'Unnamed Job';
                    const totalLow = job.totalLow !== undefined ? job.totalLow : 0;
                    const totalHigh = job.totalHigh !== undefined ? job.totalHigh : 0;
                    const laborLow = job.laborCostLow !== undefined ? job.laborCostLow : 0;
                    const laborHigh = job.laborCostHigh !== undefined ? job.laborCostHigh : 0;
                    const materialLow = job.materialCostLow !== undefined ? job.materialCostLow : 0;
                    const materialHigh = job.materialCostHigh !== undefined ? job.materialCostHigh : 0;
                    const workItems = job.workItems || [];
                    const isExpanded = expandedJobs.has(jobId);

                    const toggleJob = () => {
                      setExpandedJobs(prev => {
                        const newSet = new Set(prev);
                        if (newSet.has(jobId)) {
                          newSet.delete(jobId);
                        } else {
                          newSet.add(jobId);
                        }
                        return newSet;
                      });
                    };

                    return (
                      <div key={idx} className="border border-gray-200 rounded-lg overflow-hidden bg-white">
                        {/* Job Header */}
                        <button
                          onClick={toggleJob}
                          className="w-full p-3 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {isExpanded ? (
                                <ChevronDown className="h-4 w-4 text-gray-600" />
                              ) : (
                                <ChevronUp className="h-4 w-4 text-gray-600" />
                              )}
                              <span className="font-medium text-gray-900">{jobLabel}</span>
                              {workItems.length > 0 && (
                                <span className="text-xs text-gray-500">
                                  ({workItems.length} item{workItems.length !== 1 ? 's' : ''})
                                </span>
                              )}
                            </div>
                            <span className="font-semibold text-gray-900">
                              ${totalLow.toLocaleString()} - ${totalHigh.toLocaleString()}
                            </span>
                          </div>
                          <div className="text-xs text-gray-600 mt-1 ml-6">
                            Labor: ${laborLow.toLocaleString()}-${laborHigh.toLocaleString()} •
                            Materials: ${materialLow.toLocaleString()}-${materialHigh.toLocaleString()}
                          </div>
                        </button>

                        {/* Work Items */}
                        {isExpanded && workItems.length > 0 && (
                          <div className="border-t border-gray-200 bg-white">
                            <div className="p-2 space-y-1">
                              {workItems.map((item, itemIdx) => {
                                const itemName = item.description || item.name || 'Unnamed Item';
                                const itemTotalLow = item.totalLow || 0;
                                const itemTotalHigh = item.totalHigh || 0;
                                const itemLaborLow = item.laborCostLow || 0;
                                const itemLaborHigh = item.laborCostHigh || 0;
                                const itemMaterialLow = item.materialCostLow || 0;
                                const itemMaterialHigh = item.materialCostHigh || 0;

                                return (
                                  <div key={itemIdx} className="p-2 bg-gray-50 rounded text-xs">
                                    <div className="flex items-center justify-between mb-1">
                                      <span className="font-medium text-gray-800">{itemName}</span>
                                      <span className="font-semibold text-gray-900">
                                        ${itemTotalLow.toLocaleString()} - ${itemTotalHigh.toLocaleString()}
                                      </span>
                                    </div>
                                    <div className="text-xs text-gray-500 ml-4">
                                      Labor: ${itemLaborLow.toLocaleString()}-${itemLaborHigh.toLocaleString()} •
                                      Materials: ${itemMaterialLow.toLocaleString()}-${itemMaterialHigh.toLocaleString()}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    );
  }

  // Stage C: Refinement In Progress
  if (isRefining && questionnairesLoaded && questionnaireStats && !allQuestionnairesComplete) {
    const qe = sow.quickEstimate!;

    // Calculate totals from breakdown if not available at top level
    const breakdown = qe.breakdown || [];
    const totalCostLow = qe.totalCostLow || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.totalLow || 0), 0);
    const totalCostHigh = qe.totalCostHigh || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.totalHigh || 0), 0);
    const totalLaborLow = qe.totalLaborLow || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.laborCostLow || 0), 0);
    const totalLaborHigh = qe.totalLaborHigh || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.laborCostHigh || 0), 0);
    const totalMaterialLow = qe.totalMaterialLow || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.materialCostLow || 0), 0);
    const totalMaterialHigh = qe.totalMaterialHigh || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.materialCostHigh || 0), 0);

    return (
      <Card>
        <CardHeader className="p-4 md:p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <DollarSign className="h-4 w-4 md:h-5 md:w-5" />
              Your Estimate
            </CardTitle>
            <Badge className="bg-blue-100 text-blue-800 border-blue-200">
              🔄 Refining
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0 space-y-4">
          {/* Total Range */}
          <div className="text-center py-4">
            <div className="text-3xl md:text-4xl font-bold text-gray-900">
              ${totalCostLow.toLocaleString()} - ${totalCostHigh.toLocaleString()}
            </div>
            <div className="text-xs md:text-sm text-gray-600 mt-1">Getting more accurate...</div>
          </div>

          {/* Labor & Material Split */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs text-gray-600 mb-1">💼 Labor</div>
              <div className="text-base md:text-lg font-semibold text-gray-900">
                ${totalLaborLow.toLocaleString()} - ${totalLaborHigh.toLocaleString()}
              </div>
            </div>
            <div className="p-3 md:p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs text-gray-600 mb-1">🔨 Materials</div>
              <div className="text-base md:text-lg font-semibold text-gray-900">
                ${totalMaterialLow.toLocaleString()} - ${totalMaterialHigh.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Continue Button */}
          <Button
            onClick={onRefineEstimate}
            className="w-full"
            size="lg"
          >
            Continue Refinement →
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Stage D: Refined Estimate (After all questionnaires complete)
  if (isRefining && allQuestionnairesComplete) {
    const qe = sow.quickEstimate!;

    // Calculate totals from breakdown if not available at top level
    const breakdown = qe.breakdown || [];
    const totalCostLow = qe.totalCostLow || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.totalLow || 0), 0);
    const totalCostHigh = qe.totalCostHigh || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.totalHigh || 0), 0);
    const totalLaborLow = qe.totalLaborLow || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.laborCostLow || 0), 0);
    const totalLaborHigh = qe.totalLaborHigh || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.laborCostHigh || 0), 0);
    const totalMaterialLow = qe.totalMaterialLow || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.materialCostLow || 0), 0);
    const totalMaterialHigh = qe.totalMaterialHigh || breakdown.reduce((sum: number, job: JobEstimate) => sum + (job.materialCostHigh || 0), 0);

    return (
      <Card>
        <CardHeader className="p-4 md:p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <DollarSign className="h-4 w-4 md:h-5 md:w-5" />
              Your Estimate
            </CardTitle>
            <Badge className="bg-green-100 text-green-800 border-green-200">
              ✅ Ready for Final Estimate
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0 space-y-4">
          {/* Total Range */}
          <div className="text-center py-4">
            <div className="text-3xl md:text-4xl font-bold text-gray-900">
              ${totalCostLow.toLocaleString()} - ${totalCostHigh.toLocaleString()}
            </div>
            <div className="text-xs md:text-sm text-gray-600 mt-1">All work areas analyzed</div>
          </div>

          {/* Labor & Material Split */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs text-gray-600 mb-1">💼 Labor</div>
              <div className="text-base md:text-lg font-semibold text-gray-900">
                ${totalLaborLow.toLocaleString()} - ${totalLaborHigh.toLocaleString()}
              </div>
            </div>
            <div className="p-3 md:p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs text-gray-600 mb-1">🔨 Materials</div>
              <div className="text-base md:text-lg font-semibold text-gray-900">
                ${totalMaterialLow.toLocaleString()} - ${totalMaterialHigh.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Confidence Indicators */}
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span>All work areas refined</span>
            </div>
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span>Measurements confirmed</span>
            </div>
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span>Materials specified</span>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="space-y-2">
            <Button
              onClick={onGenerateEstimate}
              disabled={generatingProjectEstimate}
              className="w-full"
              size="lg"
            >
              {generatingProjectEstimate ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Generating...
                </>
              ) : (
                'Generate Final Detailed Estimate'
              )}
            </Button>

            <button
              onClick={onViewBreakdown}
              className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium py-2"
            >
              View Detailed Breakdown →
            </button>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Stage E: Final Detailed Estimate Available
  // Use totalEstimate if available, otherwise fall back to quickEstimate for display
  if (hasDetailedEstimate && (sow.totalEstimate || sow.quickEstimate)) {
    const te = sow.totalEstimate || sow.quickEstimate!;
    const initialLow = sow.quickEstimate?.totalCostLow;
    const initialHigh = sow.quickEstimate?.totalCostHigh;

    // Support both 'jobs' and 'breakdown' - for totalEstimate vs quickEstimate
    const jobs: JobEstimate[] = (te as any).jobs || (te as any).breakdown || [];

    // If no jobs data, don't try to render this section
    if (jobs.length === 0) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
            <div className="text-sm text-gray-600">Loading estimate...</div>
          </CardContent>
        </Card>
      );
    }

    // Helper to calculate job subtotal if not provided
    const getJobSubtotal = (job: JobEstimate): { low: number; high: number } => {
      // Use totals if available
      if (job.totalLow !== undefined && job.totalHigh !== undefined) {
        return { low: job.totalLow, high: job.totalHigh };
      }

      if (job.subtotal) return job.subtotal;

      if (!job.workItems || job.workItems.length === 0) {
        return { low: 0, high: 0 };
      }

      const low = job.workItems.reduce((sum: number, item: EstimateLineItem) => {
        if (item.totalLow !== undefined) return sum + item.totalLow;
        if (!item.laborCostLow) return sum;
        const materialCost = item.materialCostLow !== undefined
          ? item.materialCostLow
          : (item.materials ? item.materials.reduce((matSum: number, mat: Material) =>
              matSum + (mat.lowCostPerUnit * mat.quantityLow), 0) : 0);
        return sum + item.laborCostLow + materialCost;
      }, 0);

      const high = job.workItems.reduce((sum: number, item: EstimateLineItem) => {
        if (item.totalHigh !== undefined) return sum + item.totalHigh;
        if (!item.laborCostHigh) return sum;
        const materialCost = item.materialCostHigh !== undefined
          ? item.materialCostHigh
          : (item.materials ? item.materials.reduce((matSum: number, mat: Material) =>
              matSum + (mat.highCostPerUnit * mat.quantityHigh), 0) : 0);
        return sum + item.laborCostHigh + materialCost;
      }, 0);

      return { low, high };
    };

    // Calculate totals from jobs
    const totalLow = jobs.reduce((sum: number, job: JobEstimate) => sum + getJobSubtotal(job).low, 0);
    const totalHigh = jobs.reduce((sum: number, job: JobEstimate) => sum + getJobSubtotal(job).high, 0);

    const laborLow = jobs.reduce((sum: number, job: JobEstimate) => {
      if (job.laborCostLow !== undefined) return sum + job.laborCostLow;
      return sum + (job.workItems || []).reduce((itemSum: number, item: EstimateLineItem) => itemSum + (item.laborCostLow || 0), 0);
    }, 0);

    const laborHigh = jobs.reduce((sum: number, job: JobEstimate) => {
      if (job.laborCostHigh !== undefined) return sum + job.laborCostHigh;
      return sum + (job.workItems || []).reduce((itemSum: number, item: EstimateLineItem) => itemSum + (item.laborCostHigh || 0), 0);
    }, 0);

    const materialLow = jobs.reduce((sum: number, job: JobEstimate) => {
      if (job.materialCostLow !== undefined) return sum + job.materialCostLow;
      return sum + (job.workItems || []).reduce((itemSum: number, item: EstimateLineItem) => {
        if (item.materialCostLow !== undefined) return itemSum + item.materialCostLow;
        return itemSum + (item.materials ? item.materials.reduce((matSum: number, mat: Material) =>
          matSum + (mat.lowCostPerUnit * mat.quantityLow), 0) : 0);
      }, 0);
    }, 0);

    const materialHigh = jobs.reduce((sum: number, job: JobEstimate) => {
      if (job.materialCostHigh !== undefined) return sum + job.materialCostHigh;
      return sum + (job.workItems || []).reduce((itemSum: number, item: EstimateLineItem) => {
        if (item.materialCostHigh !== undefined) return itemSum + item.materialCostHigh;
        return itemSum + (item.materials ? item.materials.reduce((matSum: number, mat: Material) =>
          matSum + (mat.highCostPerUnit * mat.quantityHigh), 0) : 0);
      }, 0);
    }, 0);

    return (
      <Card>
        <CardHeader className="p-4 md:p-6">
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2 text-base md:text-lg">
              <DollarSign className="h-4 w-4 md:h-5 md:w-5" />
              Your Estimate
            </CardTitle>
            <Badge className="bg-green-100 text-green-800 border-green-200">
              ✅ Refined
            </Badge>
          </div>
        </CardHeader>
        <CardContent className="p-4 md:p-6 pt-0 space-y-4">
          {/* Total Range with improvement indicator */}
          <div className="text-center py-4">
            <div className="text-3xl md:text-4xl font-bold text-gray-900">
              ${totalLow.toLocaleString()} - ${totalHigh.toLocaleString()}
            </div>
            {initialLow && initialHigh && (
              <div className="text-xs md:text-sm text-gray-600 mt-1">
                ↑ Tightened from ${initialLow.toLocaleString()}-${initialHigh.toLocaleString()}
              </div>
            )}
          </div>

          {/* Labor & Material Split */}
          <div className="grid grid-cols-2 gap-3">
            <div className="p-3 md:p-4 bg-blue-50 rounded-lg border border-blue-200">
              <div className="text-xs text-gray-600 mb-1">💼 Labor</div>
              <div className="text-base md:text-lg font-semibold text-gray-900">
                ${laborLow.toLocaleString()} - ${laborHigh.toLocaleString()}
              </div>
              <div className="text-xs text-gray-500 mt-1">
                ({jobs.reduce((sum: number, job: JobEstimate) =>
                  sum + (job.workItems || []).reduce((itemSum: number, item: EstimateLineItem) => itemSum + (item.laborEstimateLow || 0), 0), 0)}-
                {jobs.reduce((sum: number, job: JobEstimate) =>
                  sum + (job.workItems || []).reduce((itemSum: number, item: EstimateLineItem) => itemSum + (item.laborEstimateHigh || 0), 0), 0)} hours)
              </div>
            </div>
            <div className="p-3 md:p-4 bg-green-50 rounded-lg border border-green-200">
              <div className="text-xs text-gray-600 mb-1">🔨 Materials</div>
              <div className="text-base md:text-lg font-semibold text-gray-900">
                ${materialLow.toLocaleString()} - ${materialHigh.toLocaleString()}
              </div>
            </div>
          </div>

          {/* Confidence Indicators */}
          <div className="space-y-1.5 text-sm">
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span>All areas analyzed</span>
            </div>
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span>Measurements confirmed</span>
            </div>
            <div className="flex items-center gap-2 text-green-700">
              <CheckCircle className="h-4 w-4" />
              <span>Materials specified</span>
            </div>
          </div>

          {/* View Breakdown Toggle */}
          <button
            onClick={onViewBreakdown}
            className="w-full text-sm text-blue-600 hover:text-blue-700 font-medium flex items-center justify-center gap-1"
          >
            {showBreakdown ? (
              <>Hide detailed breakdown <ChevronUp className="h-4 w-4" /></>
            ) : (
              <>See detailed breakdown <ChevronDown className="h-4 w-4" /></>
            )}
          </button>
        </CardContent>
      </Card>
    );
  }

  // Fallback: Loading state
  return (
    <Card>
      <CardContent className="p-8 text-center">
        <Loader2 className="h-8 w-8 animate-spin mx-auto mb-4 text-blue-600" />
        <div className="text-sm text-gray-600">Loading estimate...</div>
      </CardContent>
    </Card>
  );
};
